import ComposeBottomActionBar, { ComposeBottomActionBarProps } from './ComposeBottomActionBar';
import ComposeBottomEditorSendHost from './ComposeBottomEditorSendHost';
import ComposeBottomSendDiscard from './ComposeBottomSendDiscard';
import ComposeComponentProps from './ComposeComponentProps';
import datapoints from '../datapoints';
import createAttachmentHandler from '../utils/createAttachmentHandler';
import focusToEditor from '../utils/focusToEditor';
import logAttachMenuData from '../utils/logAttachMenuData';
import { observer } from 'mobx-react';
import { DirectionalHint } from 'office-ui-fabric-react/lib/common/DirectionalHint';
import { IContextualMenuProps } from 'office-ui-fabric-react/lib/ContextualMenu';
import { IconType } from 'office-ui-fabric-react/lib/Icon';
import { getComposeHostItemIndex, lazyInitializeAddinsForItem } from 'owa-addins-core';
import { populateCommandBarWithAddins } from 'owa-addins-surface-actions';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { logUsage } from 'owa-analytics';
import { AttachmentFileList } from 'owa-attachment-file-types';
import { attachmentMultiMenuEntry } from 'owa-attachment-multi-menu-actions';
import { CreateAttachmentsProperties } from 'owa-attachment-well-data';
import { preloadUploadFolder, UploadFolderMailboxType } from 'owa-attachments-upload-folder';
import { Ribbon } from 'owa-editor-ribbonplugin';
import expressionStore from 'owa-expressions-store/lib/store/expressionStore';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isGroupTableSelected } from 'owa-group-common';
import { keydownHandler } from 'owa-hotkeys';
import { addInitialAction } from 'owa-initial-actions';
import { lazyCreateMailComposeAdapter } from 'owa-mail-addins';
import shouldAddinsPopulate from 'owa-mail-addins/lib/utils/shouldAddinsPopulate';
import { loadAttachmentSuggestions } from 'owa-mail-compose-store';
import discardCompose from 'owa-mail-compose-store/lib/actions/discardCompose';
import protectMessage from 'owa-mail-compose-store/lib/actions/protectMessage';
import setImportance from 'owa-mail-compose-store/lib/actions/setImportance';
import setIsDirty from 'owa-mail-compose-store/lib/actions/setIsDirty';
import switchBodytype from 'owa-mail-compose-store/lib/actions/switchBodyType';
import toggleFrom from 'owa-mail-compose-store/lib/actions/toggleFrom';
import toggleRibbon from 'owa-mail-compose-store/lib/actions/toggleRibbon';
import trySaveMessage from 'owa-mail-compose-store/lib/actions/trySaveMessage';
import updateDockingHeight from 'owa-mail-compose-store/lib/plugins/docking/actions/updateDockingHeight';
import { getStore } from 'owa-mail-compose-store/lib/store/composeStore';
import { isPublicFolderComposeViewState } from 'owa-mail-compose-store/lib/utils/createPublicFolderComposeViewState';
import handleOnSend from 'owa-mail-compose-store/lib/utils/handleOnSend';
import shouldDisableEncryptionButton from 'owa-mail-compose-store/lib/utils/shouldDisableEncryptionButton';
import isReadingPaneCardUIEnabled from 'owa-mail-feature-flags/lib/isReadingPaneCardUIEnabled';
import { getCommands } from 'owa-mail-hotkeys';
import { showMessageOptionsDialog } from 'owa-mail-message-options-view';
import { getEditorSupport } from 'owa-mail-preferred-editor';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import { ComposeActionKey } from 'owa-outlook-service-options';
import ImportanceType from 'owa-service/lib/contract/ImportanceType';
import Item from 'owa-service/lib/contract/Item';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { preloadSharingDialogModule } from 'owa-sharing-dialog';
import strings from 'owa-strings/owa-mail-strings';
import { getPaletteAsRawColors } from 'owa-theme';
import { DarkModeContext } from 'owa-theme';
import { isBrowserIE } from 'owa-user-agent/lib/userAgent';
import * as React from 'react';
import getAttachmentCommandItems, {
    getDefaultButtonsList,
} from '../utils/getAttachmentCommandItems';
import {
    lazyInitializeAttachmentFilePicker,
    AttachmentLocalComputerFilePicker,
    FilePickerAction,
    FileSelectionFilter,
} from 'owa-attachment-filepicker';
import {
    getComposeStaticActions,
    getComposeActionDisplayName,
    getComposeActionIconName,
    SurfaceActionItem,
    StaticAction,
    logUserAction,
} from 'owa-surface-actions';
import {
    getBottomDockingHeight,
    getTopDockingHeight,
} from 'owa-mail-compose-store/lib/plugins/docking/utils/getDockingHeight';
import {
    getOptionsForFeature,
    OwsOptionsFeatureType,
    SurfaceActionsOptions,
} from 'owa-outlook-service-options';
import {
    ENCRYPT_GUID,
    ENCRYPT_DNF_GUID,
    IRM_DEFAULT_GUID,
} from 'owa-mail-compose-store/lib/store/schema/EncryptionGUID';

const styles = require('./ComposeBottom.scss');
const classNames = require('classnames/bind').bind(styles);

export interface ComposeBottomProps extends ComposeComponentProps {
    isHidden: boolean;
}

@observer
export default class ComposeBottom extends React.Component<ComposeBottomProps, {}> {
    private attachmentInlineFilePicker: any;
    private attachmentLocalComputerFilePicker: any;
    private attachmentLocalComputerToCloudFilePicker: any;
    private sendDiscard: ComposeBottomSendDiscard;
    private actionBar: ComposeBottomActionBar;
    private commandBarDividerKey = 0;
    static contextType = DarkModeContext;

    private createCommandBarItems(): ComposeBottomActionBarProps {
        const surfaceItems: SurfaceActionItem[] = [];
        const overflowItems: SurfaceActionItem[] = [];

        const staticActions = getComposeStaticActions();

        const surfaceActionsOptions = getOptionsForFeature<SurfaceActionsOptions>(
            OwsOptionsFeatureType.SurfaceActions
        );

        staticActions.forEach(action => {
            const commandBarItem = this.tryCreateStaticActionCommandBarItem(action);
            if (commandBarItem) {
                // If we were able to create a command bar item, check if the user has it in their surface actions.
                if (
                    surfaceActionsOptions.composeSurfaceActions.indexOf(
                        action.key as ComposeActionKey
                    ) >= 0
                ) {
                    // If they do, push it to the surface.
                    commandBarItem.isPinned = true;
                    if (commandBarItem.subMenuProps) {
                        commandBarItem.subMenuProps.items.forEach(subItem => {
                            subItem.isPinned = true;
                        });
                    }
                    surfaceItems.push(commandBarItem);
                } else {
                    // If they don't, push it to the overflow and override any custom rendering.
                    commandBarItem.onRender = null;
                    commandBarItem.renderedInOverflow = true;
                    if (commandBarItem.subMenuProps) {
                        commandBarItem.subMenuProps.items.forEach(subItem => {
                            subItem.renderedInOverflow = true;
                        });
                    }
                    overflowItems.push(commandBarItem);
                }
            }
        });

        // Push add-ins to the command bar if the add-ins flight is on and we're not in groups.
        if (shouldAddinsPopulate(this.props.viewState)) {
            populateCommandBarWithAddins(
                ExtensibilityModeEnum.MessageCompose,
                this.getItem(),
                this.props.viewState.composeId,
                surfaceActionsOptions.composeSurfaceAddins,
                surfaceItems,
                overflowItems,
                false /* isEndAligned */,
                this.commandBarDividerKey,
                styles.addinCommandBarItem /* className */
            );

            lazyCreateMailComposeAdapter.import().then(createMailComposeAdapter => {
                const adapter = createMailComposeAdapter(this.props.viewState);
                lazyInitializeAddinsForItem.import().then(initializeAddinsForItem => {
                    // initialize add-ins (not initialized in Compose if message was encrypted when opened)
                    initializeAddinsForItem(
                        getComposeHostItemIndex(this.props.viewState.composeId),
                        adapter
                    );
                });
            });
        }

        return { items: surfaceItems, overflowItems: overflowItems };
    }

    private tryCreateStaticActionCommandBarItem(action: StaticAction): SurfaceActionItem {
        if (action.hideInGroup && isGroupTableSelected()) {
            return null;
        }
        const actionKey = action.key as ComposeActionKey;
        let displayName = getComposeActionDisplayName(actionKey);
        const { fallbackIconName, isImage } = getComposeActionIconName(actionKey);
        let isDisabled = false;
        let canCheck = false;
        let isChecked = false;
        let clickHandler: (
            ev?: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>
        ) => boolean | void;
        let subMenuProps: IContextualMenuProps;
        let customClassName: string;
        let customRender: (className: string) => JSX.Element;
        const isPlainText = this.props.viewState.bodyType != 'HTML';
        const storeState = getStore();
        const isPublicFolderPostAction = isPublicFolderComposeViewState(this.props.viewState);
        const userConfiguration = getUserConfiguration();

        switch (actionKey) {
            case 'AddAttachment':
                isDisabled =
                    !userConfiguration.AttachmentPolicy.ClassicAttachmentsEnabled &&
                    !userConfiguration.AttachmentPolicy.ReferenceAttachmentsEnabled;
                subMenuProps = {
                    items: getAttachmentCommandItems(
                        () => this.attachmentLocalComputerFilePicker,
                        () => this.attachmentLocalComputerToCloudFilePicker,
                        this.props.plugins && this.props.plugins.inlineImagePlugin,
                        this.props.viewState
                    ),
                    onMenuOpened: () => {
                        loadAttachmentSuggestions(this.props.viewState);

                        // Log that the Attach button itself was clicked.
                        logUsage(
                            datapoints.AttachButtonClicked.datapoint.name,
                            datapoints.AttachButtonClicked.datapoint.customData(true /* isMail */)
                        );

                        // Log info about the buttons shown in the menu.
                        logAttachMenuData(
                            this.props.viewState.referenceItemId,
                            getDefaultButtonsList()
                        );
                    },
                    onMenuDismissed: () => {
                        // Log that the menu has been dismissed.
                        logUsage(
                            datapoints.AttachButtonMenuClosed.datapoint.name,
                            datapoints.AttachButtonMenuClosed.datapoint.customData(
                                true /* isMail */
                            )
                        );
                    },
                    directionalHint: DirectionalHint.topLeftEdge,
                    directionalHintFixed: true,
                };
                customClassName = styles.attachmentCommandBarButtonWithSubMenu;
                break;
            case 'AddInlineImage':
                clickHandler = this.onOpenInlineFilePicker;
                isDisabled = isPlainText;
                break;
            case 'AddEmoji':
                clickHandler = this.onToggleEmojiPicker;
                isDisabled = isPlainText;
                canCheck = true;
                isChecked =
                    expressionStore.expressionPickerViewState &&
                    expressionStore.expressionPickerViewState.isOpen;
                break;
            case 'ToggleRibbon':
                isDisabled = isPlainText;
                displayName = this.props.viewState.isRibbonShown
                    ? strings.formattingOptionsTooltipHide
                    : strings.formattingOptionsTooltip; // Override default string based on actual state
                clickHandler = this.onToggleRibbon;
                canCheck = true;
                isChecked = this.props.viewState.isRibbonShown;
                break;
            case 'ProtectMessage':
                // We bifurcate here based on consumer status, changing from a nested menu (consumer)
                // To adding an info bar with a unique UI for changing protection (enterprise)
                // This logic should match that in ComposeCommandingBar.tsx
                if (isConsumer()) {
                    subMenuProps = {
                        items: [
                            {
                                name: strings.encryptMenuText,
                                key: 'Encrypt',
                                onClick: () => {
                                    protectMessage(
                                        this.props.viewState,
                                        storeState.complianceConfigurations,
                                        ENCRYPT_GUID
                                    );
                                },
                                disabled: shouldDisableEncryptionButton(
                                    this.props.viewState,
                                    ENCRYPT_GUID
                                ),
                            },
                            {
                                name: strings.encryptDisableForwardingMenuText,
                                key: 'EncryptDNF',
                                onClick: () => {
                                    protectMessage(
                                        this.props.viewState,
                                        storeState.complianceConfigurations,
                                        ENCRYPT_DNF_GUID
                                    );
                                },
                                disabled: shouldDisableEncryptionButton(
                                    this.props.viewState,
                                    ENCRYPT_DNF_GUID
                                ),
                            },
                        ],
                    };
                } else {
                    clickHandler = () => {
                        protectMessage(
                            this.props.viewState,
                            storeState.complianceConfigurations,
                            IRM_DEFAULT_GUID
                        );
                    };
                }
                isDisabled = shouldDisableEncryptionButton(this.props.viewState);
                customClassName = styles.commandBarButtonWithSubMenu;
                break;
            case 'SaveDraft':
                if (isPublicFolderPostAction) {
                    break;
                }
                clickHandler = (
                    event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
                    item?: SurfaceActionItem
                ) => {
                    this.logCSAUserAction(actionKey, item);
                    trySaveMessage(this.props.viewState);
                    focusToEditor(this.props.viewState);
                };
                break;
            case 'InsertSignature':
                if (isPublicFolderPostAction) {
                    break;
                }
                clickHandler = this.onInsertSignature;
                break;
            case 'ShowFrom':
                isDisabled = this.props.viewState.fromViewState.isFromReadOnly;
                const isFromShown = this.props.viewState.fromViewState.isFromShown;
                displayName = !isFromShown ? strings.showFrom : strings.hideFrom; // Override default string based on actual state
                clickHandler = (
                    event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
                    item?: SurfaceActionItem
                ) => {
                    this.logCSAUserAction(actionKey, item, isFromShown ? 'Hide' : 'Show');
                    toggleFrom(this.props.viewState);
                    focusToEditor(this.props.viewState);
                };
                break;
            case 'ShowMessageOptions':
                if (isPublicFolderPostAction) {
                    break;
                }
                clickHandler = (
                    event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
                    item?: SurfaceActionItem
                ) => {
                    showMessageOptionsDialog(this.props.viewState);
                };
                break;
            case 'SetImportance':
                subMenuProps = {
                    items: [
                        {
                            name: strings.highImportanceMenuText,
                            key: 'High',
                            onClick: (
                                event:
                                    | React.KeyboardEvent<HTMLElement>
                                    | React.MouseEvent<HTMLElement>,
                                item?: SurfaceActionItem
                            ) => {
                                this.onSetImportance('High', item);
                            },
                            canCheck: true,
                            checked: this.props.viewState.importance === 'High',
                        },
                        {
                            name: strings.normalImportanceMenuText,
                            key: 'Normal',
                            onClick: (
                                event:
                                    | React.KeyboardEvent<HTMLElement>
                                    | React.MouseEvent<HTMLElement>,
                                item?: SurfaceActionItem
                            ) => {
                                this.onSetImportance('Normal', item);
                            },
                            canCheck: true,
                            checked: this.props.viewState.importance === 'Normal',
                        },
                        {
                            name: strings.lowImportanceMenuText,
                            key: 'Low',
                            onClick: (
                                event:
                                    | React.KeyboardEvent<HTMLElement>
                                    | React.MouseEvent<HTMLElement>,
                                item?: SurfaceActionItem
                            ) => {
                                this.onSetImportance('Low', item);
                            },
                            canCheck: true,
                            checked: this.props.viewState.importance === 'Low',
                        },
                    ],
                };
                customClassName = styles.commandBarButtonWithSubMenu;
                break;
            case 'SwitchBodyType':
                if (!this.props.viewState.isInlineCompose) {
                    displayName = isPlainText
                        ? strings.switchToHTMLMenuItem
                        : strings.switchToPlainTextMenuItem;
                    clickHandler = this.onSwitchBodyType;
                }
                break;
            case 'AccChecker':
                isDisabled = isPlainText;
                canCheck = true;
                isChecked = this.props.viewState.accChecker.isOpen;
                clickHandler = this.onToggleAccChecker;
                break;
        }

        if (clickHandler || subMenuProps) {
            const iconType = isImage ? IconType.image : IconType.default;
            const imageProps = isImage
                ? { shouldFadeIn: false, src: fallbackIconName, className: styles.commandBarImage }
                : null;
            const iconClassName = classNames(styles.commandBarIcon, {
                isImage: isImage,
                isIE: isBrowserIE(),
            });
            return {
                key: actionKey,
                name: displayName,
                iconProps: {
                    iconName: fallbackIconName,
                    iconType: iconType,
                    imageProps: imageProps,
                    className: iconClassName,
                },
                disabled: isDisabled,
                canCheck: canCheck,
                checked: isChecked,
                onClick: clickHandler,
                subMenuProps: subMenuProps,
                className: customClassName,
                onRender: customRender,
                iconOnly: true,
                ariaLabel: displayName,
            };
        } else {
            return null;
        }
    }

    componentDidMount() {
        addInitialAction(this.props.initialActions, async () => {
            const initializeAttachmentFilePicker = await lazyInitializeAttachmentFilePicker.import();
            initializeAttachmentFilePicker();
        });
        addInitialAction(this.props.initialActions, async () => {
            const attachmentWell = this.props.viewState.attachmentWell;
            // we preload the default folder which is used to upload to the
            // onedrive in the upload and share scenario
            if (isGroupTableSelected() && attachmentWell && attachmentWell.groupId) {
                preloadUploadFolder(
                    this.props.viewState.attachmentWell.groupId,
                    UploadFolderMailboxType.Group
                );
            } else {
                preloadUploadFolder();
            }
        });

        if (isFeatureEnabled('doc-attachment-sharingDialog')) {
            addInitialAction(this.props.initialActions, async () => {
                // We have to load the strings package before we can load the sharing dialog.
                // The .js file for the sharing dialog is also large, so to decrease response time on clicking
                // change permissions, we preload everything now.
                preloadSharingDialogModule.import();
            });
        }
    }

    render() {
        const { viewState, plugins, isHidden } = this.props;
        const isDarkMode = this.context;
        const {
            isInlineCompose,
            isRibbonShown,
            ribbon,
            docking: { useStickyDocking },
        } = viewState;
        const handlerForAttachFromComputer = attachmentMultiMenuEntry.computer.getCreateAttachmentHandler(
            viewState.attachmentWell,
            (files, properties) =>
                plugins &&
                createAttachmentHandler(files, plugins.inlineImagePlugin, viewState, properties)
        );

        const handlerForUploadAndShareFromCloud = attachmentMultiMenuEntry.uploadAndShareFromCloud.getCreateAttachmentHandler(
            viewState.attachmentWell,
            (files, properties) =>
                plugins &&
                createAttachmentHandler(files, plugins.inlineImagePlugin, viewState, properties)
        );

        const bottomDockingStyle = isDarkMode
            ? null
            : {
                  // This spacer is supposed to use theme colors.
                  //
                  // Since the Compose wrapper is already in a reset context,
                  // set these colors directly from the raw palette.
                  backgroundColor: getPaletteAsRawColors().neutralLighterAlt,
              };

        return (
            <div
                className={classNames({
                    composeBottomContainer: true,
                    cardUI: isReadingPaneCardUIEnabled(),
                    isInlineCompose: isInlineCompose,
                    sticky: useStickyDocking,
                    isHidden: !useStickyDocking && isHidden,
                })}>
                {getEditorSupport(viewState).formattingRibbon && isRibbonShown && plugins && (
                    <Ribbon
                        className={styles.ribbonRow}
                        ribbonPlugin={plugins.ribbonPlugin}
                        viewState={ribbon}
                        skipInitialResize={true}
                        focusToButtonBeforeDropDown={true}
                        isRtl={getUserConfiguration().SessionSettings.IsUserCultureRightToLeft}
                    />
                )}
                {this.renderBottomBar()}
                {useStickyDocking && !isInlineCompose && (
                    <div className={styles.stickyBottomFiller} style={bottomDockingStyle} />
                )}
                <AttachmentLocalComputerFilePicker
                    ref={ref => (this.attachmentInlineFilePicker = ref)}
                    fileSelectionFilter={FileSelectionFilter.Images}
                    action={FilePickerAction.Insert}
                    createAttachmentHandler={this.createAttachmentHandler}
                />
                <AttachmentLocalComputerFilePicker
                    ref={ref => (this.attachmentLocalComputerFilePicker = ref)}
                    fileSelectionFilter={FileSelectionFilter.None}
                    action={FilePickerAction.Attach}
                    createAttachmentHandler={handlerForAttachFromComputer}
                />
                <AttachmentLocalComputerFilePicker
                    ref={ref => (this.attachmentLocalComputerToCloudFilePicker = ref)}
                    fileSelectionFilter={FileSelectionFilter.None}
                    action={FilePickerAction.Attach}
                    createAttachmentHandler={handlerForUploadAndShareFromCloud}
                />
            </div>
        );
    }

    resize() {
        // Resize the sendDiscard first so that the actionBar has an accurate
        // measure of its available space
        if (this.sendDiscard) {
            this.sendDiscard.resize();
        }
        if (this.actionBar) {
            this.actionBar.resize();
        }
    }

    private renderBottomBar() {
        const { viewState } = this.props;

        // Normal case, show Send/Discard button and toolbar
        const commandBarData = this.createCommandBarItems();
        return (
            <ComposeBottomEditorSendHost viewState={viewState}>
                <ComposeBottomSendDiscard
                    ref={ref => (this.sendDiscard = ref)}
                    viewState={viewState}
                    composeId={this.props.viewState.composeId}
                    onSend={this.onSend}
                    onDiscard={this.onDiscard}>
                    {/* Make the bottom bar conditional on editor support */}
                    {getEditorSupport(viewState).bottomActionBar ? (
                        <ComposeBottomActionBar
                            ref={ref => (this.actionBar = ref)}
                            {...commandBarData}
                        />
                    ) : null}
                </ComposeBottomSendDiscard>
            </ComposeBottomEditorSendHost>
        );
    }

    @keydownHandler(getCommands().sendMail)
    sendMessage() {
        handleOnSend(this.props.viewState);
    }

    @keydownHandler(getCommands().saveDraft)
    saveDraft() {
        trySaveMessage(this.props.viewState);
    }

    @keydownHandler(
        getCommands().discardDraft,
        true /*stopPropagation*/,
        true /*preventDefault*/,
        true /*allowHotkeyOnTextFields*/
    )
    discardDraft() {
        discardCompose(this.props.viewState);
    }

    private onSend = () => {
        handleOnSend(this.props.viewState);
    };

    private onDiscard = () => {
        discardCompose(this.props.viewState);
    };

    private createAttachmentHandler = (
        files: AttachmentFileList,
        shouldShare: boolean = false,
        isInline: boolean = false
    ) => {
        const viewState = this.props.viewState;
        const inlineImagePlugin = this.props.plugins && this.props.plugins.inlineImagePlugin;
        const baseProperties = { isInline, isHiddenInline: false };
        const properties: CreateAttachmentsProperties = shouldShare
            ? { ...baseProperties, shouldShare: true }
            : { ...baseProperties, shouldShare: false };

        createAttachmentHandler(files, inlineImagePlugin, viewState, properties);
    };

    private onToggleRibbon = (
        event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item?: SurfaceActionItem
    ) => {
        const { viewState } = this.props;
        toggleRibbon(viewState);

        this.logCSAUserAction('ToggleRibbon', item, viewState.isRibbonShown ? 'On' : 'Off');

        const { docking, isInlineCompose, isRibbonShown } = viewState;
        updateDockingHeight(
            docking,
            getTopDockingHeight(isInlineCompose),
            getBottomDockingHeight(isRibbonShown, isInlineCompose)
        );

        focusToEditor(this.props.viewState);
    };

    private onToggleEmojiPicker = (
        event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item?: SurfaceActionItem
    ) => {
        const emojiPlugin = isFeatureEnabled('cmp-newEmoji-plugin')
            ? this.props.plugins.emojiPickerPlugin
            : this.props.plugins.emojiPlugin;
        let dataPointSuffix = 'Close';
        if (
            emojiPlugin.isImported() &&
            expressionStore.expressionPickerViewState &&
            expressionStore.expressionPickerViewState.isOpen
        ) {
            emojiPlugin.getPlugin().then(plugin => plugin.closePicker());
        } else {
            emojiPlugin.addOnImportedCallback(plugin => plugin.openPicker());
            emojiPlugin.getPlugin();
            dataPointSuffix = 'Open';
        }

        this.logCSAUserAction('AddEmoji', item, dataPointSuffix);
    };

    private onToggleAccChecker = (
        event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item?: SurfaceActionItem
    ) => {
        const accCheckerPlugin = this.props.plugins.accCheckerPlugin;
        const accCheckerViewState = this.props.viewState.accChecker;
        let dataPointSuffix = 'Close';
        if (accCheckerPlugin.isImported() && accCheckerViewState.isOpen) {
            accCheckerPlugin.getPlugin().then(plugin => plugin.closeChecker());
        } else {
            accCheckerPlugin.addOnImportedCallback(plugin => plugin.openChecker());
            accCheckerPlugin.getPlugin();
            dataPointSuffix = 'Open';
        }

        this.logCSAUserAction('AccChecker', item, dataPointSuffix);
    };

    private onOpenInlineFilePicker = (
        event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item?: SurfaceActionItem
    ) => {
        this.logCSAUserAction('AddInlineImage', item);
        if (!this.props.viewState.itemId) {
            trySaveMessage(this.props.viewState);
        }

        this.attachmentInlineFilePicker.getInnerComponent().click();
    };

    private onInsertSignature = (
        event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item?: SurfaceActionItem
    ) => {
        this.logCSAUserAction('InsertSignature', item);
        const { viewState } = this.props;
        const { bodyType } = viewState;
        this.props.plugins.contentOperationPlugin.insertSignature(bodyType);
        setIsDirty(viewState);
    };

    private onSetImportance = (importance: ImportanceType, item?: SurfaceActionItem) => {
        this.logCSAUserAction('SetImportance', item, importance);
        setImportance(this.props.viewState, importance);
        focusToEditor(this.props.viewState);
    };

    private onSwitchBodyType = (
        event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
        item?: SurfaceActionItem
    ) => {
        const suffix = this.props.viewState.bodyType == 'HTML' ? 'Plain' : 'HTML'; // Reversed to log the type being set.
        this.logCSAUserAction('SwitchBodyType', item, suffix);
        switchBodytype(this.props.viewState);
    };

    private getItem(): Item {
        // Return the item if it is available
        const { itemId } = this.props.viewState;
        if (!itemId) {
            return null;
        }

        return getMailStore().items.get(itemId.Id);
    }

    private logCSAUserAction(
        action: ComposeActionKey,
        item: SurfaceActionItem,
        suffix: string = ''
    ) {
        logUserAction(true /* isCompose */, `${action}${suffix}`, item);
    }
}
